import {Country} from '@hconnect/apiclient'
import {GenericInput, GenericSelect} from '@hm/fundamental'
import React from 'react'
import {Controller, FieldValues} from 'react-hook-form'

import {useTranslation} from '../../../hooks/useTranslation'
import {UserInformationInputSkeleton} from '../Skeleton/UserInformationInputSkeleton'

import {UserInformationProps} from './types'

type UserLanguageInputProps<T extends FieldValues> = UserInformationProps<T> & {
  selectedCountry?: Country
}

export const UserLanguageInput = <T extends FieldValues>({
  readonlyMode,
  formMethods,
  selectedCountry,
  isLoading,
  name
}: UserLanguageInputProps<T>) => {
  const {t} = useTranslation()
  const errors = formMethods.formState.errors
  const values = formMethods.watch()

  if (isLoading) {
    return <UserInformationInputSkeleton />
  }

  const defaultLocaleHelperText = (() => {
    if (!values.country) {
      return t('userInformationForm.defaultLocaleHelperText')
    }
    if (!values.defaultLocale && formMethods.formState.isSubmitted && errors.defaultLocale) {
      return t('userInformationForm.defaultLocaleIsRequired')
    }
  })()

  return (
    <>
      {readonlyMode ? (
        <GenericInput
          data-test-id="userInformationForm-defaultLocale"
          label={t('userInformationForm.defaultLocale')}
          value={
            selectedCountry?.locales.find((locale) => locale.code === values.defaultLocale)?.name
          }
          isReadOnly={true}
        />
      ) : (
        <Controller
          name={name}
          control={formMethods.control}
          rules={{validate: (value) => (value ? true : t('errorMessages.FIELD_REQUIRED'))}}
          render={({field}) => (
            <GenericSelect
              {...field}
              data-cy={`userInformationForm-${field.name}`}
              label={t('userInformationForm.defaultLocale')}
              setValue={(value) => field.onChange(value)}
              value={(field.value as string) || '-'}
              isReadOnly={readonlyMode}
              isTouched={true}
              error={defaultLocaleHelperText}
              values={
                selectedCountry?.locales.map((locale) => ({
                  value: locale.code,
                  label: locale.name
                })) ?? []
              }
              className="w-full"
            />
          )}
        />
      )}
    </>
  )
}
